import type { TFunction } from 'i18next';
import { ApiError } from '../api/client';

/**
 * errorText is THE ONE place a failure becomes a sentence on the screen.
 *
 * ⚠ THE SERVER'S OWN MESSAGE IS WHAT IS SHOWN. The Personal-Area API returns a Russian
 * message per error code (`internal/i18n`), and the login route deliberately returns
 * ONE flat refusal for six different causes — unknown login, wrong password, no
 * password set, an unreadable stored password, an archived card, a correct password from
 * a forbidden address — because distinguishing them turns the form into a
 * subscriber-enumeration oracle over logins derived from contract numbers.
 *
 * So this function must never map a code to a friendlier local wording, and must never
 * add a distinction of its own («такого логина нет», «пароль неверен»). A second
 * catalogue on the client is a catalogue that drifts from the one that carries the
 * security property.
 *
 * TWO cases are the client's own, and only two:
 *
 *   - the request never reached the server (`isNetworkError`), where there is no server
 *     message to show;
 *   - the server answered without a usable envelope (a bare 404/502 from something in
 *     front of it), where `client.ts` synthesises `http_<status>` / «HTTP 404». Showing
 *     that to a subscriber is showing him nothing, so the generic unavailable text is
 *     used instead — and the synthetic code is what makes the two cases separable.
 */
export function errorText(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    if (err.isNetworkError) return t('common.networkError');
    if (err.code.startsWith('http_') || !err.message) return t('common.unavailableText');
    return err.message;
  }
  return t('common.unavailableText');
}
